import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  Sparkle, CheckCircle, Circle, Trophy, Feather, Fire, ArrowRight, X, ShareNetwork, PaperPlaneTilt,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import ListenTranslateBar from "@/components/ListenTranslateBar";

// /challenges — Choose a 7-day eco challenge, tap-to-complete each day,
// jot a journal note, unlock a badge. Auth-gated (guests see a sign-in prompt).

// Deep-link each day's "Observe now" button to the most relevant Observations
// channel so a challenger can immediately record their observation in the
// right feed on NaturePulse.
const DAY_CHANNEL = {
  1: "plants",      // Find a Plant
  2: "trees",       // Meet a Tree
  3: "birds",       // Listen & Look for Birds
  4: "insects",     // Discover the Small World
  5: "sky",         // Look Up
  6: "water",       // Find Water
  7: "seasonal",    // What Is Changing?
};

// Anchor-per-day tab strip + expanded prompt panel for the 7-Day Nature
// Challenge. Visible to every visitor — no sign-in required — so anyone
// can read what each day asks them to do before committing to the streak.
function PublicNatureChallengePreview({ templates, mine, onStart, onOpen, onInvite, invitedKey }) {
  const tpl = templates.find((t) => t.key === "nature-observer");
  const [active, setActive] = useState(1);
  if (!tpl) return null;
  const day = tpl.days.find((d) => d.n === active) || tpl.days[0];
  const activeChallenge = mine?.challenges?.find((c) => c.key === tpl.key && !c.badge_earned);
  const isInvited = invitedKey === tpl.key;
  return (
    <section className="max-w-6xl mx-auto px-5 md:px-8 mb-14" data-testid="nature-challenge-preview">
      <div className="relative overflow-hidden rounded-[28px] border border-black/10 bg-gradient-to-br from-[#FBF2E1] via-white to-[#F5EBDC] p-6 md:p-10 shadow-[0_20px_60px_-30px_rgba(24,18,10,0.35)]">
        {/* Ambient decorative flare */}
        <div className="pointer-events-none absolute -top-24 -right-16 h-64 w-64 rounded-full bg-[color:var(--np-brand,#B85F2A)]/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-32 -left-24 h-72 w-72 rounded-full bg-emerald-500/10 blur-3xl" />

        <div className="relative flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="font-mono-tech text-[10px] tracking-[0.36em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-3 flex items-center gap-2">
              <Sparkle size={12} weight="fill" /> 7-Day Nature Challenge · the daily prompts
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight max-w-3xl">
              Seven days. Seven observations.<br/>
              <span className="text-[color:var(--np-brand,#B85F2A)]">Discover the nature around you.</span>
            </h2>
            <div className="mt-4 flex flex-wrap gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white border border-black/10 px-3 py-1 font-mono-tech text-[10px] tracking-[0.24em] uppercase">
                <Fire size={11} weight="fill" className="text-[color:var(--np-brand,#B85F2A)]"/> 7-day streak
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white border border-black/10 px-3 py-1 font-mono-tech text-[10px] tracking-[0.24em] uppercase">
                <Trophy size={11} weight="fill" className="text-emerald-600"/> observer badge
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white border border-black/10 px-3 py-1 font-mono-tech text-[10px] tracking-[0.24em] uppercase">
                🌿 journal-friendly
              </span>
            </div>
          </div>
        </div>

        <p className="relative mt-6 text-[#3A3830] font-serif-display text-base md:text-lg max-w-4xl leading-relaxed">
          You don't have to travel to a national park or deep into a forest to discover nature. It may be growing beside your driveway, flying over your home, crawling across a flower, floating in a pond, or forming in the clouds above you. For the next seven days, spend a few minutes each day observing, photographing and documenting one part of the natural world around you.
        </p>

        {/* Day tab strip */}
        <div className="relative mt-8 -mx-1 overflow-x-auto pb-1" data-testid="nature-challenge-tabs">
          <div className="flex gap-2 px-1 min-w-max">
            {tpl.days.map((d) => (
              <button
                key={d.n}
                onClick={() => setActive(d.n)}
                data-testid={`nature-challenge-tab-${d.n}`}
                aria-selected={active === d.n}
                className={`group inline-flex items-center gap-2.5 rounded-2xl px-4 py-3 border transition-all ${
                  active === d.n
                    ? "bg-[#241209] text-[#F5EBDC] border-[#241209] shadow-[0_8px_24px_-12px_rgba(24,18,10,0.6)] scale-[1.02]"
                    : "bg-white border-black/10 hover:border-[color:var(--np-brand,#B85F2A)]/50 hover:-translate-y-0.5"
                }`}
              >
                <span className="text-2xl leading-none">{d.emoji || "•"}</span>
                <span className="flex flex-col items-start">
                  <span className={`font-mono-tech text-[9px] tracking-[0.28em] uppercase ${active === d.n ? "text-[#E4A15C]" : "text-[color:var(--np-brand,#B85F2A)]"}`}>Day {d.n}</span>
                  <span className="font-serif-display text-sm leading-tight hidden sm:inline">{d.title}</span>
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Active day panel */}
        <div
          key={day.n}
          className="relative mt-6 rounded-2xl border border-black/10 bg-white p-6 md:p-8 shadow-[0_10px_40px_-24px_rgba(24,18,10,0.45)]"
          data-testid={`nature-challenge-panel-${day.n}`}
        >
          <div className="flex items-start gap-5">
            <div className="hidden md:flex h-16 w-16 items-center justify-center rounded-2xl bg-[#F5EBDC] text-4xl shrink-0">
              {day.emoji}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3">
                <div className="md:hidden text-3xl leading-none">{day.emoji}</div>
                <div>
                  <div className="font-mono-tech text-[10px] tracking-[0.36em] uppercase text-[color:var(--np-brand,#B85F2A)]">
                    Day {day.n} of 7
                  </div>
                  <h3 className="mt-0.5 font-serif-display text-3xl md:text-4xl leading-tight tracking-tight">
                    {day.title}
                  </h3>
                </div>
              </div>
              <p className="mt-4 text-[15px] md:text-[17px] leading-relaxed text-[#241209] font-serif-display max-w-3xl">
                {day.body}
              </p>
              <div className="mt-6 flex flex-wrap gap-2.5">
                {activeChallenge ? (
                  <button
                    onClick={() => onOpen(activeChallenge)}
                    data-testid="nature-challenge-continue"
                    className="inline-flex items-center gap-2 rounded-full bg-[#241209] text-[#F5EBDC] px-5 py-2.5 font-mono-tech text-[11px] tracking-[0.28em] uppercase hover:opacity-90"
                  >
                    <Sparkle size={13} weight="fill" /> Continue day {activeChallenge.current_day}
                  </button>
                ) : (
                  <button
                    onClick={() => onStart(tpl.key)}
                    data-testid="nature-challenge-start"
                    className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-mono-tech text-[11px] tracking-[0.28em] uppercase transition ${
                      isInvited
                        ? "bg-[color:var(--np-brand,#B85F2A)] text-white ring-2 ring-[color:var(--np-brand,#B85F2A)]/40"
                        : "bg-[color:var(--np-brand,#B85F2A)] text-white hover:opacity-90"
                    }`}
                  >
                    <Sparkle size={13} weight="fill" /> {isInvited ? "Accept invite" : "Start a challenge"}
                  </button>
                )}
                <Link
                  to={`/observations?channel=${DAY_CHANNEL[day.n] || ""}`}
                  data-testid={`nature-challenge-observe-${day.n}`}
                  className="inline-flex items-center gap-2 rounded-full bg-[#241209] text-[#F5EBDC] px-5 py-2.5 font-mono-tech text-[11px] tracking-[0.28em] uppercase hover:opacity-90"
                >
                  Observe now <ArrowRight size={12} weight="bold" />
                </Link>
                <button
                  onClick={() => onInvite(tpl)}
                  data-testid="nature-challenge-invite"
                  className="inline-flex items-center gap-2 rounded-full border border-black/20 px-5 py-2.5 font-mono-tech text-[11px] tracking-[0.28em] uppercase hover:bg-white/70"
                >
                  <PaperPlaneTilt size={12} weight="fill" /> Challenge a friend
                </button>
                <button
                  onClick={() => setActive(Math.min(7, day.n + 1))}
                  disabled={day.n === 7}
                  data-testid="nature-challenge-next"
                  className="inline-flex items-center gap-2 rounded-full border border-black/20 px-5 py-2.5 font-mono-tech text-[11px] tracking-[0.28em] uppercase hover:bg-white/70 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Next day <ArrowRight size={12} weight="bold" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="relative mt-6 flex flex-wrap items-center gap-3 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#5c554a]">
          <Trophy size={12} weight="fill" className="text-[color:var(--np-brand,#B85F2A)]" />
          Complete all seven days to earn the 7-Day Nature Observer badge.
        </div>
      </div>
    </section>
  );
}

export default function Challenges() {
  const { user, requireSignIn } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [mine, setMine] = useState({ challenges: [], badges: 0, max_streak: 0 });
  const [openChallenge, setOpenChallenge] = useState(null);
  const [journalDay, setJournalDay] = useState(null);
  const [journalNote, setJournalNote] = useState("");

  useEffect(() => {
    http.get("/challenges/templates").then((r) => setTemplates(r.data?.templates || []));
    if (user) http.get("/challenges/mine").then((r) => setMine(r.data || { challenges: [] })).catch(() => {});
  }, [user]);

  const refreshMine = () =>
    http.get("/challenges/mine").then((r) => setMine(r.data || { challenges: [] })).catch(() => {});

  const start = async (key) => {
    if (!requireSignIn("start a challenge")) return;
    try {
      const { data } = await http.post("/challenges/start", { key });
      setMine((m) => {
        const others = m.challenges.filter((c) => c.id !== data.challenge.id);
        return { ...m, challenges: [data.challenge, ...others] };
      });
      setOpenChallenge(data.challenge);
      toast.success(data.resumed ? "Resumed challenge" : "Challenge started");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start");
    }
  };

  const complete = async (challenge, day) => {
    try {
      const note = journalDay === day ? journalNote.trim() : null;
      const { data } = await http.post(`/challenges/${challenge.id}/complete`, { day, note: note || null });
      setOpenChallenge(data.challenge);
      setMine((m) => ({ ...m, challenges: m.challenges.map((c) => c.id === data.challenge.id ? data.challenge : c) }));
      setJournalDay(null); setJournalNote("");
      if (data.challenge.badge_earned) {
        toast.success(`Badge earned — ${data.challenge.badge_emoji}`, {
          description: "Challenge a friend to complete their own 7 days.",
          action: {
            label: "Invite",
            onClick: () => {
              const tpl = templates.find((t) => t.key === data.challenge.key);
              if (tpl) inviteFriend(tpl);
            },
          },
          duration: 8000,
        });
      }
    } catch (e) { toast.error("Couldn't save"); }
  };

  const activeTemplate = openChallenge ? templates.find((t) => t.key === openChallenge.key) : null;

  // Invite a friend to the same challenge — uses the native share sheet on
  // mobile (WhatsApp / SMS / Email / X etc.) and falls back to clipboard on
  // desktop browsers that don't expose navigator.share.
  const inviteFriend = async (t) => {
    const origin = typeof window !== "undefined" ? window.location.origin : "https://naturepulse.net";
    const url = `${origin}/challenges?invite=${encodeURIComponent(t.key)}`;
    const text = `Join me on the ${t.title} — ${t.tagline} 🌿`;
    try {
      if (typeof navigator !== "undefined" && navigator.share) {
        await navigator.share({ title: t.title, text, url });
        return;
      }
      if (typeof navigator !== "undefined" && navigator.clipboard) {
        await navigator.clipboard.writeText(`${text}\n${url}`);
        toast.success("Invite link copied — share it with a friend!");
        return;
      }
      window.prompt("Copy this invite link", `${text}\n${url}`);
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Couldn't open share sheet");
    }
  };

  // If a friend opened /challenges?invite=<key>, auto-scroll and highlight
  // the invited challenge card + surface a start prompt.
  const [invitedKey] = useState(() => {
    if (typeof window === "undefined") return null;
    return new URLSearchParams(window.location.search).get("invite");
  });
  useEffect(() => {
    if (!invitedKey || templates.length === 0) return;
    const tpl = templates.find((t) => t.key === invitedKey);
    if (tpl) toast.info(`A friend invited you to ${tpl.title}. Tap “Start challenge” to join.`);
  }, [invitedKey, templates]);

  return (
    <main className="bg-[#F5EBDC] text-[#1B4332] pt-32 md:pt-36 pb-24 min-h-screen">
      <div className="max-w-[1200px] mx-auto px-5 md:px-10">
        <ListenTranslateBar text="Community challenges — join seasonal events and observe the planet together." testidPrefix="ltbar-challenges" />
      </div>
      {/* Hero */}
      <section className="max-w-3xl mx-auto px-5 md:px-8 mb-8">
        <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-3">
          / 7-Day Eco Challenges
        </div>
        <h1 className="font-serif-display text-5xl md:text-7xl leading-[0.9] tracking-tighter">
          Seven days. One habit.
        </h1>
        <p className="mt-4 text-base md:text-lg text-[#3A3830] font-serif-display max-w-2xl">
          Streak-tracked, badge-rewarded, journal-friendly. Choose a challenge, complete a small daily action, and reflect. Earn the badge on day seven.
        </p>
        {user && (mine.badges > 0 || mine.max_streak > 0) && (
          <div className="mt-5 flex flex-wrap gap-3">
            <div className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 border border-emerald-600/40 bg-emerald-50 text-emerald-800 font-mono-tech text-[10px] tracking-[0.28em] uppercase">
              <Trophy size={12} weight="fill" /> {mine.badges} badge{mine.badges === 1 ? "" : "s"}
            </div>
            <div className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 border border-[color:var(--np-brand,#B85F2A)]/40 bg-orange-50 text-orange-900 font-mono-tech text-[10px] tracking-[0.28em] uppercase">
              <Fire size={12} weight="fill" /> {mine.max_streak} day streak
            </div>
          </div>
        )}
      </section>

      {/* Public 7-day preview — Nature Challenge is now the hero of this page */}
      <PublicNatureChallengePreview
        templates={templates}
        mine={mine}
        onStart={start}
        onOpen={setOpenChallenge}
        onInvite={inviteFriend}
        invitedKey={invitedKey}
      />

      {/* More 7-day challenges — compact strip */}
      <section className="max-w-6xl mx-auto px-5 md:px-8 mb-10" data-testid="more-challenges">
        <div className="flex items-baseline justify-between mb-4">
          <h3 className="font-serif-display text-2xl md:text-3xl tracking-tight">More 7-day challenges</h3>
          <span className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]/70">
            pick another streak
          </span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {templates.filter((t) => t.key !== "nature-observer").map((t) => {
            const active = mine.challenges.find((c) => c.key === t.key && !c.badge_earned);
            return (
              <div
                key={t.key}
                data-testid={`challenge-tpl-${t.key}`}
                className="group rounded-2xl border border-black/10 bg-white hover:border-[color:var(--np-brand,#B85F2A)] transition-all hover:-translate-y-0.5 p-4 flex items-start gap-3"
              >
                <div className="text-3xl leading-none shrink-0">{t.badge_emoji}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-serif-display text-lg leading-tight">{t.title}</div>
                  <div className="text-xs text-[#3A3830]/80 mt-0.5 line-clamp-2">{t.tagline}</div>
                  <div className="mt-3 flex items-center gap-2 flex-wrap">
                    {active ? (
                      <button
                        onClick={() => setOpenChallenge(active)}
                        data-testid={`challenge-open-${t.key}`}
                        className="inline-flex items-center gap-1 rounded-full bg-[#241209] text-[#F5EBDC] px-3 py-1.5 font-mono-tech text-[9px] tracking-[0.24em] uppercase"
                      >
                        Continue day {active.current_day} <ArrowRight size={11} weight="bold" />
                      </button>
                    ) : (
                      <button
                        onClick={() => start(t.key)}
                        data-testid={`challenge-start-${t.key}`}
                        className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 font-mono-tech text-[9px] tracking-[0.24em] uppercase transition ${
                          invitedKey === t.key
                            ? "bg-[color:var(--np-brand,#B85F2A)] text-white ring-2 ring-[color:var(--np-brand,#B85F2A)]/40"
                            : "border border-[#241209] hover:bg-[#241209] hover:text-[#F5EBDC]"
                        }`}
                      >
                        {invitedKey === t.key ? "Accept invite" : "Start"}
                      </button>
                    )}
                    <button
                      onClick={() => inviteFriend(t)}
                      data-testid={`challenge-invite-${t.key}`}
                      className="inline-flex items-center gap-1 rounded-full border border-black/20 px-3 py-1.5 font-mono-tech text-[9px] tracking-[0.24em] uppercase text-[#241209]/80 hover:border-[#241209] hover:text-[#241209]"
                      aria-label="Challenge a friend"
                      title="Challenge a friend"
                    >
                      <PaperPlaneTilt size={11} weight="fill" /> Invite
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Earned badges strip */}
      {user && mine.challenges.some((c) => c.badge_earned) && (
        <section className="max-w-5xl mx-auto px-5 md:px-8 mb-8">
          <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-3">/ Your badges</div>
          <ul className="flex flex-wrap gap-2">
            {mine.challenges.filter((c) => c.badge_earned).map((c) => (
              <li key={c.id} className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 bg-emerald-50 border border-emerald-600/30 text-emerald-900">
                <span className="text-xl leading-none">{c.badge_emoji}</span>
                <span className="text-sm">{c.title}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {!user && (
        <section className="max-w-3xl mx-auto px-5 md:px-8 mt-6">
          <div className="rounded-xl border border-dashed border-black/20 p-5 text-center">
            <div className="font-serif-display text-xl">Sign in to save your streak.</div>
            <div className="text-sm text-[#3A3830] mt-1">Challenges, journal and badges are stored to your NaturePulse account.</div>
          </div>
        </section>
      )}

      {/* Detail sheet */}
      <AnimatePresence>
        {openChallenge && activeTemplate && (
          <>
            <motion.div className="fixed inset-0 z-[70] bg-black/60" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        onClick={() => setOpenChallenge(null)} />
            <motion.div
              data-testid="challenge-sheet"
              className="fixed z-[71] inset-x-0 bottom-0 md:inset-0 md:m-auto md:h-[86dvh] md:max-w-[560px] rounded-t-2xl md:rounded-2xl bg-[#F5EBDC] border border-black/10 overflow-hidden flex flex-col"
              initial={{ y: 400, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 400, opacity: 0 }}
              transition={{ type: "spring", stiffness: 240, damping: 26 }}
              style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
            >
              <div className="px-5 pt-4 pb-3 border-b border-black/10 flex items-center justify-between">
                <div className="min-w-0">
                  <div className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)]">
                    Streak · {openChallenge.streak}/7 {openChallenge.badge_earned && <span className="text-emerald-600">· badge earned {openChallenge.badge_emoji}</span>}
                  </div>
                  <div className="font-serif-display text-2xl leading-tight mt-0.5 truncate">{openChallenge.title}</div>
                </div>
                <div className="flex items-center gap-1 shrink-0 ml-3">
                  <button
                    onClick={() => inviteFriend(activeTemplate)}
                    data-testid="challenge-sheet-invite"
                    className="inline-flex items-center gap-1 rounded-full border border-[#1B4332]/25 px-3 py-1.5 font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[#1B4332]/80 hover:border-[#1B4332] hover:text-[#1B4332]"
                    title="Challenge a friend"
                    aria-label="Challenge a friend"
                  >
                    <ShareNetwork size={12} weight="fill" /> Invite
                  </button>
                  <button aria-label="Close" onClick={() => setOpenChallenge(null)} className="p-1.5"><X size={16} weight="bold" /></button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-5 space-y-2.5">
                {activeTemplate.days.map((d) => {
                  const done = openChallenge.completed_days.includes(d.n);
                  const expanded = journalDay === d.n;
                  return (
                    <div key={d.n} className={`rounded-xl border ${done ? "border-emerald-500/50 bg-emerald-50/40" : "border-black/10 bg-white/60"}`}>
                      <div className="p-3.5 flex items-start gap-3">
                        <button onClick={() => complete(openChallenge, d.n)} data-testid={`challenge-day-${d.n}`}>
                          {done ? <CheckCircle size={22} weight="fill" className="text-emerald-500" /> : <Circle size={22} weight="regular" className="text-[color:var(--np-muted)]" />}
                        </button>
                        <div className="flex-1 min-w-0">
                          <div className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[color:var(--np-muted)]">Day {d.n}</div>
                          <div className="font-serif-display text-lg leading-tight">{d.title}</div>
                          <div className="text-sm text-[#3A3830] mt-0.5">{d.body}</div>
                          <button
                            className="mt-2 font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] hover:underline"
                            onClick={() => { setJournalDay(expanded ? null : d.n); setJournalNote(""); }}
                            data-testid={`challenge-journal-${d.n}`}
                          >
                            {expanded ? "Cancel note" : "Add journal note"}
                          </button>
                          {expanded && (
                            <div className="mt-2 flex gap-2">
                              <input
                                data-testid={`challenge-note-${d.n}`}
                                value={journalNote}
                                onChange={(e) => setJournalNote(e.target.value)}
                                placeholder="What did you notice today?"
                                className="flex-1 bg-transparent border border-black/15 rounded-full px-3 py-1.5 text-sm"
                              />
                              <button
                                onClick={() => complete(openChallenge, d.n)}
                                className="rounded-full bg-[#1B4332] text-[#F5EBDC] px-3 py-1.5 font-mono-tech text-[9px] tracking-[0.28em] uppercase"
                              >Save</button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}

                {openChallenge.journal?.length > 0 && (
                  <div className="mt-6">
                    <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-muted)] mb-2">Your journal</div>
                    <ul className="space-y-2">
                      {openChallenge.journal.slice().reverse().map((j, i) => (
                        <li key={i} className="rounded-lg border border-black/10 bg-white/70 p-3">
                          <div className="font-mono-tech text-[9px] tracking-[0.22em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-0.5">
                            {j.day_title ? `Day ${j.day} · ${j.day_title}` : (j.day ? `Day ${j.day}` : "Note")} · {new Date(j.created_at).toLocaleDateString()}
                          </div>
                          <div className="text-sm text-[#1B4332]">{j.note}</div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </main>
  );
}
